import { useMessagesStore } from "@/atoms/playground";
import { conversationsApi } from "@/services/conversation-service";
import { ConversationMessageData } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const getPendingMessages = <T extends { id: string }>(messages: Array<T>) =>
  messages.filter((message) => message.id.startsWith("temp-"));

type ConversationMessageLike = {
  id: string;
  conversation_id?: string;
  content?: unknown;
  role?: string;
  is_streaming?: boolean;
};

const mergeConversationMessages = <
  T extends ConversationMessageLike,
  U extends ConversationMessageLike,
>(
  persistedMessages: Array<T>,
  pendingMessages: Array<U>,
) => {
  const combinedMessages = [...persistedMessages, ...pendingMessages] as Array<
    T | U
  >;
  const seenIds = new Set<string>();

  return combinedMessages.filter((message) => {
    if (seenIds.has(message.id)) {
      return false;
    }
    seenIds.add(message.id);

    const isPersisted =
      Boolean(message.id) &&
      !message.id.startsWith("temp-") &&
      message.is_streaming === false;

    const hasMatchingTempPeer =
      combinedMessages.length > 1 &&
      combinedMessages.some(
        (candidate) =>
          candidate.id.startsWith("temp-") &&
          candidate.role === message.role &&
          candidate.conversation_id === message.conversation_id &&
          candidate.content === message.content,
      );

    if (isPersisted && hasMatchingTempPeer) {
      return false;
    }

    return true;
  });
};

export const useConversation = (id?: string) => {
  const { setMessages, setReferences } = useMessagesStore();
  const location = useLocation();
  const hydratedConversationIdRef = useRef<string | undefined>(undefined);

  const { data, isLoading, isError, error } = useQuery<ConversationMessageData>(
    {
      queryKey: ["conversation", id, location.pathname.split("/")[1]],
      queryFn: () =>
        conversationsApi.getConversation(
          id ?? "",
        ) as Promise<ConversationMessageData>,
      enabled: !!id,
      staleTime: Infinity,
    },
  );

  useEffect(() => {
    if (!id) {
      hydratedConversationIdRef.current = undefined;
      setMessages([]);
      setReferences({});
      return;
    }

    if (!data) {
      // conversation not fetched yet: drop the previous conversation's
      // messages, keep this one's optimistic (temp-) messages, and leave
      // the guard open so hydration runs once data lands
      hydratedConversationIdRef.current = undefined;
      const pending = getPendingMessages(
        useMessagesStore.getState().messages,
      ).filter((message) => message.conversation_id === id);
      setMessages(pending);
      setReferences({});
      return;
    }

    if (hydratedConversationIdRef.current === id) {
      return;
    }

    const currentMessages = useMessagesStore.getState().messages;
    const pendingMessages = getPendingMessages(currentMessages).filter(
      (message) => message.conversation_id === id,
    );
    // Optimistic (temp-) messages are written into the conversation query
    // cache during streaming, so data.messages may already contain them.
    // Dedupe by id and suppress a persisted copy when a matching temp peer
    // already exists in the merged set.
    const nextMessages = mergeConversationMessages(
      data.messages,
      pendingMessages,
    );

    setMessages(nextMessages);
    setReferences(data.references ?? {});
    hydratedConversationIdRef.current = id;
  }, [data, id, setMessages, setReferences]);

  useEffect(() => {
    if (isError) {
      toast.error((error as Error)?.message || "Failed to load conversation");
    }
  }, [isError, error]);

  return { data, isLoading, isError, error };
};
